/**
 * Referral code capture.
 *
 * A referral code arrives as `?ref=` on a link somebody shared. It is captured
 * before any route matches, kept for a limited number of days, and replayed on
 * whichever sign-in the visitor completes. Two rules drive everything here:
 *
 * - First touch wins. A code already on the device is never overwritten by a
 *   later link, so the friend who was clicked first keeps the credit.
 * - Capture is invisible. A malformed or unknown code is dropped silently; the
 *   registration itself is never blocked or annotated by any of this.
 *
 * The code is public information and never acts as a credential: it only ever
 * travels in the sign-in body and the click report.
 */

import {
  LIMITS,
  REFERRAL_CAPTURE_TTL_SECONDS,
  REFERRAL_CODE_LENGTH,
  type ReferralChannel,
} from '@shared/constants'

const CAPTURE_STORAGE_KEY = 'toh.referral.capture'
/** Codes already reported in this tab, so a refresh does not double count. */
const REPORTED_SESSION_KEY = 'toh.referral.reported'

export interface ReferralCaptureState {
  code: string
  channel: ReferralChannel
  /** Unix seconds the code was first seen, used to expire it. */
  capturedAt: number
}

/** Referral fields appended to a sign-in request body. */
export interface ReferralFields {
  ref: string
  refChannel: ReferralChannel
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

/** Storage access that never throws: private mode and quotas are not fatal. */
function readCaptureRaw(): string | null {
  try {
    return window.localStorage.getItem(CAPTURE_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Normalises a code taken from a URL.
 *
 * The same shape the Worker accepts: eight characters of upper case letters and
 * digits, with separators and lower case tolerated on input.
 */
export function normalizeReferralCode(value: string): string | null {
  if (value.length > LIMITS.referralCodeMaxLength) {
    return null
  }
  const normalized = value.replace(/[\s-]+/g, '').toUpperCase()
  if (normalized.length !== REFERRAL_CODE_LENGTH || !/^[A-Z0-9]+$/.test(normalized)) {
    return null
  }
  return normalized
}

/**
 * Channel a link was opened from.
 *
 * Event share pages are the only place a share token appears; everything else is
 * a plain site visit.
 */
export function referralChannelForPath(pathname: string): ReferralChannel {
  return /^\/e\/[^/]+/.test(pathname) ? 'event_share' : 'site'
}

/** Reads the stored capture, dropping it once it is past its lifetime. */
export function readReferralCapture(): ReferralCaptureState | null {
  if (typeof window === 'undefined') {
    return null
  }
  const raw = readCaptureRaw()
  if (!raw) {
    return null
  }
  const parsed = safeParse(raw)
  if (!parsed) {
    return null
  }
  if (parsed.capturedAt + REFERRAL_CAPTURE_TTL_SECONDS <= nowSeconds()) {
    clearReferralCapture()
    return null
  }
  return parsed
}

/**
 * Referral fields to send with a sign-in.
 *
 * Returns an empty object when nothing was captured, so callers can spread it
 * into the body unconditionally.
 */
export function referralFields(): Partial<ReferralFields> {
  const capture = readReferralCapture()
  return capture ? { ref: capture.code, refChannel: capture.channel } : {}
}

/**
 * Builds a same-origin path with the capture attached as query parameters.
 *
 * The GitHub flow cannot carry the code in a request body: the account is
 * created during the provider callback, and the signed `state` that travels
 * through it is built by `GET /api/auth/github/start`. The code therefore has to
 * be part of that start URL.
 */
export function withReferralParams(path: string, params: Record<string, string> = {}): string {
  const search = new URLSearchParams(params)
  const capture = readReferralCapture()
  if (capture) {
    search.set('ref', capture.code)
    search.set('refChannel', capture.channel)
  }
  const query = search.toString()
  return query.length > 0 ? `${path}?${query}` : path
}

/**
 * Captures `?ref=` from the current address and reports the impression.
 *
 * Must be called before the first render: the query string is only guaranteed to
 * be readable there, and the write has to happen whether or not any route
 * matches.
 *
 * Two rules that are easy to confuse: the *stored* code is first-touch, so an
 * existing unexpired capture is never overwritten, while the *click* is counted
 * for every valid code the browser opens (once per code per tab). Attribution
 * follows the first link; the statistic describes all of them.
 */
export function captureReferral(): void {
  if (typeof window === 'undefined') {
    return
  }
  const params = new URLSearchParams(window.location.search)
  const raw = params.get('ref')
  if (!raw) {
    return
  }
  const code = normalizeReferralCode(raw)
  if (!code) {
    return
  }
  const channel = referralChannelForPath(window.location.pathname)

  // Counted first: the impression exists even when this link loses the
  // first-touch race and its code is not stored.
  reportClick(code, channel)

  if (readReferralCapture()) {
    return
  }

  const capture: ReferralCaptureState = { code, channel, capturedAt: nowSeconds() }
  try {
    window.localStorage.setItem(CAPTURE_STORAGE_KEY, JSON.stringify(capture))
  } catch {
    // Without storage the code simply does not survive the reload; attribution
    // falls back to the default referrer instead of breaking the page.
  }
}

/** Removes the stored capture. */
export function clearReferralCapture(): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.removeItem(CAPTURE_STORAGE_KEY)
  } catch {
    // See above: nothing to clean up when storage is unavailable.
  }
}

/**
 * Reports one impression of the link.
 *
 * The endpoint needs no session and answers with a fixed success; statistics
 * must never surface as a page error, so every failure is swallowed, including a
 * runtime without `fetch` at all. A code is reported at most once per tab, which
 * is what stops a refresh and back/forward navigation from inflating the count.
 */
function reportClick(code: string, channel: ReferralChannel): void {
  if (alreadyReported(code)) {
    return
  }
  markReported(code)

  try {
    void fetch('/api/referral/click', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code, channel }),
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {
      // Intentionally silent: a failed count is not a user-visible problem.
    })
  } catch {
    // A runtime without fetch (server rendering, a stripped test environment)
    // simply produces no statistic.
  }
}

/** Codes already reported in this tab. */
function reportedCodes(): string[] {
  try {
    const raw = window.sessionStorage.getItem(REPORTED_SESSION_KEY)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
  } catch {
    return []
  }
}

function alreadyReported(code: string): boolean {
  return reportedCodes().includes(code)
}

function markReported(code: string): void {
  try {
    const reported = reportedCodes()
    reported.push(code)
    window.sessionStorage.setItem(REPORTED_SESSION_KEY, JSON.stringify(reported))
  } catch {
    // Without session storage the counter may double count; that is acceptable
    // for a display-only statistic.
  }
}

/** Parses a stored capture, rejecting anything that is not the expected shape. */
function safeParse(raw: string): ReferralCaptureState | null {
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const record = value as Record<string, unknown>
  const { code, channel, capturedAt } = record
  if (typeof code !== 'string' || normalizeReferralCode(code) !== code) {
    return null
  }
  if (channel !== 'event_share' && channel !== 'site') {
    return null
  }
  if (typeof capturedAt !== 'number' || !Number.isFinite(capturedAt)) {
    return null
  }
  return { code, channel, capturedAt }
}
